import { Injectable, EventEmitter } from '@angular/core';
import { Router, RoutesRecognized } from '@angular/router';
import { Location } from '@angular/common';
import { debounceTime, distinctUntilChanged, filter, pairwise, firstValueFrom, Subscription } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';

import { LocationService } from '../../shared/services/location.service';
import { AppWizardState, WizardStep } from '../models/app-wizard-state';
import { Offer, OfferFamilyVolume, ProductFamily, OfferProduct, AddToCartResponse,
	ProductShortDescription, ProductDimension, VariableField, EXCLUDE_FROM_THIRD_ROW,
	TemplateVariableArray, PRODUCT_TYPE_NF, PRODUCT_TYPE_F, FIELD_THICKNESS, FIELD_WIDTH,
	FIELD_SHIPTO_PARTY, FIELD_STEEL_GRADE, FIELD_LENGTH, TemplateVariable } from '../models/offer';
import { FamilyOfProducts } from '../models/offer-wizard';
import { offerCreateDefault, offerCopyForDraft, offerHeaderIdCreate, OFFER_TYPE } from '../models/offer-utils';
import { TranslationService } from '../../shared/services/translation.service';
import { ProductConfiguration, CharacteristicValueSimpleObject,
	TemplateVariableValues } from '../../shared/models/product';
import { BocClient } from '../../shared/models/boc-client-transac';
import {
	SnackbarSubmissionFeedbackComponent
} from '../../shared/components/snackbar-submission-feedback/snackbar-submission-feedback.component';
import { SnackbarSaveDraftComponent } from '../../shared/components/snackbar-save-draft/snackbar-save-draft.component';
import {
	ProductCharacteristicsTranslationService
} from '../../shared/services/product-characteristics-translation.services';

import { OfferService, OFFER_DRAFT_REDIRECT_URL } from './offer.api.service';
import { OfferWizardDb } from './wizard.database';
import { AppOfferService } from './app-offer.service';

export const PAGE_NAME_STEPS = ['stepHeader', 'stepCart', 'stepQuantity', 'stepReview'];

@Injectable({ providedIn: 'root' })
export class AppWizardService {
	previousUrl: string | undefined;
	urlData: string[] = [];
	backViewSubscription?: Subscription;
	isDefaultFamily = false;
	familyByVolumes: OfferFamilyVolume[] = [];
	familyOfProductsList?: FamilyOfProducts[] = [];
	shipToList?: BocClient[];
	endUserList?: BocClient[];
	soldToList?: BocClient[];
	readonly variableValuesIndexedByTemplateNumber = new Map<number, Map<string, TemplateVariableValues>>();
	valuesPossiblePerFieldByTemplateNumber = new Map<number, VariableField[]>();
	optionalToleranceTemplateGroup = new Map<number, Map<string, VariableField[]>>();
	thirdRowsVariables = new Map<number, VariableField[]>();
	productsGroupedByFamily = new Map<string, number>();
	onProductRemoved = new EventEmitter();

	private state: AppWizardState;

	constructor(
		private readonly appOfferService: AppOfferService,
		private readonly offerService: OfferService,
		private readonly router: Router,
		private readonly locationService: LocationService,
		private readonly db: OfferWizardDb,
		private readonly translationService: TranslationService,
		private readonly location: Location,
		private readonly productCharacteristicsTranslationService: ProductCharacteristicsTranslationService,
		private readonly snackBar: MatSnackBar,

	) {
		const defaultOffer = offerCreateDefault();
		this.state = new AppWizardState(defaultOffer, defaultOffer);
	}

	getState() {
		return this.state;
	}

	getStateOfferForm() {
		return this.state.offer.form;
	}

	async getStateOfferFromCache() {
		if (!this.state.isValid) {
			await this.restoreCachedOffer();
		}
		return this.getStateOfferForm();
	}

	async checkWizardStateValidity(stepIndex: WizardStep, redirectToRootWizard = true) {
		// if user starts wizard via a direct URL (i.e. without going through "new", "edit", or "copy" offer)
		// then, if no offer in cache, a new offer is created
		// anyway the wizard is launched from the beginning
		// while substituating the initial global state (cf. locationService.back)
		this.state.stepIndex = stepIndex;
		if (this.state.isValid) {
			return true;
		}
		if (this.state.exiting) {
			this.state.exiting = false;
			return true;
		}
		const offerRestored = await this.restoreCachedOffer();
		if (offerRestored) {
			if (redirectToRootWizard) {
				await this.router.navigateByUrl('offer/wizard', { replaceUrl: true }); // start at beginning
			}
			return true;
		}
		const defaultOffer = offerCreateDefault();
		this.initOffer(defaultOffer, defaultOffer);
		this.locationService.back('offer/wizard');
		return false;
	}

	checkOfferTypeExists() {
		return sessionStorage.getItem(OFFER_TYPE) !== null || this.state.offer.getOfferType() !== undefined;
	}

	async saveOfferLocally() {
		return this.db.saveOffer(this.state.offer.extractOffer());
	}

	async actionNewOffer() {
		try {
			const defaultOffer = offerCreateDefault();
			this.initOffer(defaultOffer, defaultOffer);
			await this.db.removeOffer();
			const currentUrl = this.router.url;
			await (currentUrl.includes('offer/wizard')
				? this.router.navigateByUrl('/', { skipLocationChange: true }).then(async () => {
					await this.router.navigate([currentUrl]);
				}).catch((err: unknown) => {
					console.warn('unable to navigate to offer wizard', err);
				})
				: await this.router.navigateByUrl('offer/wizard'));
			return true;
		} catch (err) {
			console.error('unable to create new offer', err);
			this.appOfferService.showError();
			return false;
		}
	}

	async actionEditOffer(offerId: string) {
		try {
			const offer = await firstValueFrom(this.offerService.getOffer(offerId, true, 'NONE'));
			this.initOffer(offer, offer);
			await this.db.removeOffer();
			await this.saveOfferLocally();
			return await this.router.navigateByUrl('offer/wizard');
		} catch (err) {
			console.error('unable to edit offer', err);
			this.appOfferService.showError();
			return false;
		}
	}

	async actionCopyOffer(offerId: string) {
		try {
			const offer = await firstValueFrom(this.offerService.getOffer(offerId, true, 'NONE'));
			const draftOffer = offerCopyForDraft(offer);
			this.state = new AppWizardState(draftOffer, draftOffer);
			this.state.isValid = true;
			await this.db.removeOffer();
			await this.saveOfferLocally();
			return await this.router.navigateByUrl('offer/wizard');
		} catch (err) {
			console.error('unable to copy offer', err);
			this.appOfferService.showError();
			return false;
		}
	}

	async actionSaveCurrentOffer() {
		const stateOffer = this.state.offer;
		try {
			const offer = await firstValueFrom(
				stateOffer.form.getOfferId() === undefined
					? this.offerService.createOffer(stateOffer.extractOffer())
					: this.offerService.updateOffer(stateOffer.extractOffer())
			);
			stateOffer.updateLastSavedOffer(offer);
			this.snackBar.openFromComponent(
				SnackbarSaveDraftComponent,
				{
					data: {
						draftSavedSuccessMessage: this.translationService.getTR('eoffer.draft.saved.successfully'),
						draftEditMessageTextLine1: this.translationService.getTR('eoffer.draft.edit.message.text'),
						draftListLinkText: this.translationService.getTR('eoffer.draft.list.link'),
						draftEditMessageTextLine2: this.translationService.getTR('eoffer.draft.edit.message.line'),
						redirectUrl: OFFER_DRAFT_REDIRECT_URL
					},
					duration: 9000,
					panelClass: ['copo-snackbar-save-draft']
				}
			);
			await this.router.navigateByUrl('/home');
			return true;
		} catch (err) {
			console.error('unable to save offer', err);
			this.appOfferService.showError(this.translationService.getTR('eoffer.message.offer.not.saved'));
			return false;
		}
	}

	actionResetCurrentOffer() {
		this.state.offer.reset();
	}

	async actionGoNextStep() {
		if (this.state.stepIndex > WizardStep.REVIEW) {
			return;
		}
		const maxStepIndex = this.state.offer.computeIndexStep();
		if (this.state.stepIndex > maxStepIndex) {
			return;
		}
		// push navigation step
		this.state.stepIndex += 1;

		const url = `offer/wizard/${ PAGE_NAME_STEPS[this.state.stepIndex] }`;
		await this.router.navigateByUrl(url);
	}

	actionGoPreviousStep() {
		if (this.state.stepIndex <= WizardStep.HEADER) {
			return;
		}
		this.state.stepIndex -= 1;
		this.location.back();
	}

	actionToGoBack() {
		this.backViewSubscription = this.router.events
			.pipe(
				filter((ev): ev is RoutesRecognized => ev instanceof RoutesRecognized),
				pairwise()
			)
			.subscribe(ev => {
				for (const url of ev) {
					this.urlData.push(url.urlAfterRedirects);
				}
			});
		const disconIdx = this.urlData.indexOf('/offer/wizard/stepHeader');
		return this.urlData[disconIdx - 1];
	}

	actionCleanUrlData() {
		this.urlData = [];
	}

	actionSavePreviousPage(previousPage: string | undefined) {
		this.previousUrl = previousPage;
	}

	actionRetrievePreviousPage() {
		return this.previousUrl;
	}

	async actionMoveUsingSteps(step: WizardStep) {
		if (this.state.stepIndex <= WizardStep.HEADER) {
			return;
		}
		if (step < this.state.stepIndex) {
			const url = `offer/wizard/${ PAGE_NAME_STEPS[step] }`;
			await this.router.navigateByUrl(url);
		}
	}

	getCurrentRoute(): string {
		return this.router.url;
	}

	getPreviousFamiliesItemCount(currentFamilyKey: string) {
		const selSoldToId = <string> this.getStateOfferForm().getFormSoldTo().value;
		const productFamilies = this.getState().ui.getProductFamilies(selSoldToId);
		let itemCount = 0;
		if (productFamilies) {
			for (const productFamily of productFamilies) {
				if (productFamily.code) {
					if (productFamily.code === currentFamilyKey) {
						return itemCount;
					}
					itemCount += this.productsGroupedByFamily.get(productFamily.code) ?? 0;
				}
			}
		}
		return itemCount;
	}

	async actionSubmitOffer() {
		const stateOffer = this.state.offer;
		const offer = stateOffer.extractOffer();
		try {
			const offerId = await firstValueFrom(this.offerService.submitOffer(offer));
			offer.header.id = offerId.id;
			offer.header.externalId = offerId.externalId;
			offer.header.rfqNumber = offerId.rfqNumber;
			if (offerId.id) {
				stateOffer.form.setOfferId(offerId.id);
			}
			if (offerId.externalId) {
				stateOffer.form.setExternalOfferId(offerId.externalId);
			}
			if (offerId.rfqNumber) {
				stateOffer.form.setrfqNumber(offerId.rfqNumber);
			}
			stateOffer.updateLastSavedOffer(offer);
			history.pushState({}, '', 'offer/wizard');
			sessionStorage.setItem('copoUrlRedirect', '/offer/dashboard');
			history.go(-this.state.stepIndex - 1);
			this.snackBar.openFromComponent(
				SnackbarSubmissionFeedbackComponent,
				{
					data: {	isThanksMessage: true,
						isRedirectLink: false,
						submitrequestId: offerId.rfqNumber,
						submitRequestLabel: this.translationService.getTR('application.procurement.supplier.rfqNumber'),
						submitRequestFeedback: this.translationService.getTR('eoffer.submission.feedback'),
						submitThanksMessage: this.translationService.getTR('confirm.thanks') },
					duration: 20_000,
					panelClass: ['copo-snackbar-submission-feedback-container']
				}
			);
			return true;
		} catch (err) {
			console.error('unable to submit offer', err);
			this.appOfferService.showError(this.translationService.getTR('eoffer.message.offer.not.submit'));
			return false;
		}
	}

	actionClearWizardHistory() {
		this.state.isValid = false;
		this.state.exiting = false;
		const defaultOffer = offerCreateDefault();
		this.initOffer(defaultOffer, defaultOffer);
	}

	cleanCart() {
		this.state.offer.form.getFormProducts().removeAllProducts();
	}

	async actionShowOfferCockpit() {
		if (this.state.stepIndex !== WizardStep.REVIEW) {
			throw new Error('not allowed to force navigation to dashboard while not at confirmation step');
		}
		this.state.isValid = false;
		history.go(-this.state.stepIndex - 1);
		await this.router.navigateByUrl('offer/dashboard', { replaceUrl: true });
	}

	async actionExitWizard() {
		this.variableValuesIndexedByTemplateNumber.clear();
		this.valuesPossiblePerFieldByTemplateNumber.clear();
		this.thirdRowsVariables.clear();
		this.optionalToleranceTemplateGroup.clear();
		this.productsGroupedByFamily.clear();
		const defaultOffer = offerCreateDefault();
		this.initOffer(defaultOffer, defaultOffer);
		await this.db.removeOffer();
		return true;
	}

	clearOfferTypeFromSession() {
		sessionStorage.removeItem(OFFER_TYPE);
	}

	createSubscriptionOfferLocalAutoSave() {
		return this.state.offer.form.valueChanges.pipe(debounceTime(1000), distinctUntilChanged()).subscribe({
			next: async () => {
				await this.saveOfferLocally();
			},
			error: (err: unknown) => {
				console.error('unable to cache offer', err);
			},
		});
	}

	addToCart(
		configData: ProductConfiguration,
		itemCount: number,
		templateNumber?: string
	): AddToCartResponse {
		const formProducts = this.state.offer.form.getFormProducts();
		const productFamily: ProductFamily = {
			category: '',
			categoryDescription: '',
			subCategory: '',
			subCategoryDescription: '',
			form: '',
			formDescription: '',
			code: '',
			customerTemplate: [{}]
		};
		const productDimension: ProductDimension = {
			thickness: { value: 0, unit: '' },
			width: { value: 0, unit: '' },
		};
		const description: ProductShortDescription = {
			family: productFamily,
			steelGrade: '',
			dimension: productDimension,
		};
		const productToAdd: OfferProduct = {
			a2citemNumber: configData.a2CItemNumber,
			inquiryNumber: configData.inquiryNumber,
			configurationNumber: configData.configurationNumber,
			productType: configData.productType,
			shipToParty: <string> this.state.offer.form.getFormShipTo().value,
			endUser: <string> this.state.offer.form.getFormEndUser().value,
			offerStatus: 'DRAFT',
			feasibilityStatus: 'YES',
			description,
			templateNumber: Number(templateNumber),
			category: this.removeTranslationPrefix(configData.category),
			form: this.removeTranslationPrefix(configData.form),
			subCategory: this.removeTranslationPrefix(configData.subcategory)
		};
		if (productToAdd.productType !== PRODUCT_TYPE_NF) {
			productToAdd.steelGrade = configData.steelGradeCode;
			productToAdd.thicknessValue = Number(configData.thicknessValue);
			productToAdd.widthValue = Number(configData.widthValue);
			productToAdd.lengthValue = Number(configData.lengthValue);
		}
		if (configData.steelGrade) {
			productToAdd.steelGradeDescription = this.productCharacteristicsTranslationService
				.translate(configData.steelGrade);
		}
		if (productToAdd.productType) {
			productToAdd.productType = this.removeTranslationPrefix(productToAdd.productType);
			if (productToAdd.productType === PRODUCT_TYPE_F) {
				productToAdd.isSimulationCompleted = 'true';
			}
		}
		const varaibleFormGroupArray = this.prepareVariableFormGroup(
			Number(templateNumber),
			configData,
			itemCount
		);
		const response: AddToCartResponse = { itemAdded: false, messages: [] };
		if (itemCount > 0) {
			const products = this.generateProductsByGivenItemCount(productToAdd, varaibleFormGroupArray, itemCount);
			for (const product of products) {
				if (product.productType === PRODUCT_TYPE_F && product.uniqueRef !== undefined) {
					this.getState().ui.updateSimulationData(product.uniqueRef, configData);
				}
			}
			if (formProducts.length > 0) {
				if ((this.getProductCount() + itemCount) > 100) {
					response.itemAdded = false;
					response.messages.push(this.translationService.getTR('eorder.add.item.limit.exeeds.warning')
						.replace('[X]', String(itemCount)));
				} else {
					response.itemAdded = true;
					formProducts.addProducts(products);
				}
			} else if (itemCount > 100) {
				response.itemAdded = false;
				response.messages.push(this.translationService.getTR('eorder.add.item.limit.exeeds.warning')
					.replace('[X]', String(itemCount)));
			} else {
				response.itemAdded = true;
				formProducts.addProducts(products);
			}
		}
		return response;
	}

	getProductCount() {
		return this.state.offer.form.getFormProducts().getProductCount();
	}

	getValuesPossiblePerField(templateNumber: number): VariableField[] {
		return this.valuesPossiblePerFieldByTemplateNumber.get(templateNumber) || [];
	}

	populateValuesPossiblePerField(templateNumber: number): VariableField[] {
		let fieldKeyValues = this.valuesPossiblePerFieldByTemplateNumber.get(templateNumber);
		if (fieldKeyValues === undefined) {
			const templateAsMap = this.variableValuesIndexedByTemplateNumber.get(templateNumber);
			fieldKeyValues = [];
			if (templateAsMap) {
				for (const [key, value] of templateAsMap) {
					const fields = {
						keys: key,
						values: value.values,
						type: value.type,
						freeType: value.freeType,
						maxItems: value.maxItems,
						parentVariableName: value.parentVariableName,
						mandatory: value.mandatory,
						hidden: value.hidden,
						group: value.group,
						order: value.order
					};
					fieldKeyValues.push(fields);
				}
				this.valuesPossiblePerFieldByTemplateNumber.set(templateNumber, fieldKeyValues);
			}
		}
		return fieldKeyValues;
	}

	getVariablesForThirdRow(templateNumber: number) {
		return this.thirdRowsVariables.get(templateNumber) || [];
	}

	populateVariablesForThirdRow(templateNumber: number) {
		if (!this.thirdRowsVariables.has(templateNumber)) {
			const valuesPossiblePerField = this.getValuesPossiblePerField(templateNumber);
			this.thirdRowsVariables.set(templateNumber, this.filterVariablesForThirdRow(valuesPossiblePerField));
		}
	}

	filterVariablesForThirdRow(fieldKeyValues: VariableField[]) {
		return fieldKeyValues
			.filter(fieldKeyValue =>
				!EXCLUDE_FROM_THIRD_ROW.includes(fieldKeyValue.keys)
				&& fieldKeyValue.group === undefined
				&& fieldKeyValue.order === undefined)
			.sort((a, b) => a.keys.localeCompare(b.keys));
	}

	getOptionalToleranceFields(templateNumber: number) {
		return this.optionalToleranceTemplateGroup.get(templateNumber) || new Map<string, VariableField[]>();
	}

	populateVariablesForOptionalTolerance(templateNumber: number) {
		if (this.optionalToleranceTemplateGroup.has(templateNumber)) {
			return;
		}
		const valuesPossiblePerField = this.getValuesPossiblePerField(templateNumber);
		for (const fieldKeyValue of valuesPossiblePerField) {
			if (fieldKeyValue.group && fieldKeyValue.order) {
				let currentTemplateGroup = this.optionalToleranceTemplateGroup.get(templateNumber);
				if (currentTemplateGroup) {
					const optionalToleranceGroup = currentTemplateGroup.get(fieldKeyValue.group);
					if (optionalToleranceGroup) {
						optionalToleranceGroup.push(fieldKeyValue);
					} else {
						currentTemplateGroup.set(fieldKeyValue.group, [fieldKeyValue]);
					}
				} else {
					currentTemplateGroup = new Map<string, VariableField[]>();
					this.optionalToleranceTemplateGroup.set(templateNumber, currentTemplateGroup);
					currentTemplateGroup.set(fieldKeyValue.group, [fieldKeyValue]);
				}
			}
		}
		for (const [, value] of this.optionalToleranceTemplateGroup.entries()) {
			for (const [groupName, fields] of value.entries()) {
				const sortedFields = fields.toSorted((a, b) => a.order && b.order ? a.order - b.order : 0);
				value.set(groupName, sortedFields);
			}
		}
	}

	checkVariableValuesExistByTemplateNumberAndVariableName(templateNumber: number, variableName: string): boolean {
		const templateAsMap = this.variableValuesIndexedByTemplateNumber.get(templateNumber);
		return templateAsMap?.has(variableName) || false;
	}

	prepareVariableFormGroupArray(
		formArray: FormArray,
		arrayObject: CharacteristicValueSimpleObject[],
		variableArray: TemplateVariableArray,
		mandatory: boolean
	) {
		for (const entryVariableName of Object.getOwnPropertyNames(variableArray.properties)) {
			// contentChildFieldName from templateDetails
			for (const obj of arrayObject) {
				if (obj[entryVariableName]) { // contentChildFieldName value from configData
					let value = obj[entryVariableName];
					if (value && !variableArray.properties[entryVariableName].free
						&& typeof value === 'string') {
						value = this.removeTranslationPrefix(value);
					}
					if (mandatory) {
						formArray.controls.push(new FormControl(
							value || '',
							Validators.required
						));
					} else {
						formArray.controls.push(new FormControl(value || ''));
					}
				}
			}
		}
		if (formArray.controls.length === 0) {
			if (mandatory) {
				formArray.controls.push(new FormControl('', Validators.required));
			} else {
				formArray.controls.push(new FormControl(''));
			}
		}
	}

	private generateProductsByGivenItemCount(
		product: OfferProduct,
		variableFormGroups?: FormGroup[],
		itemCount?: number
	) {
		const products: OfferProduct[] = [];
		if (itemCount && variableFormGroups) {
			for (let i = 0; i < itemCount; i++) {
				const productToAdd: OfferProduct = {
					description: product.description,
					offerStatus: product.offerStatus,
					feasibilityStatus: product.feasibilityStatus,
					a2citemNumber: product.a2citemNumber,
					inquiryNumber: product.inquiryNumber,
					quantity: product.quantity,
					steelGrade: product.steelGrade,
					steelGradeDescription: product.steelGradeDescription,
					thicknessValue: product.thicknessValue,
					widthValue: product.widthValue,
					lengthValue: product.lengthValue,
					shipToParty: product.shipToParty,
					endUser: product.endUser,
					category: product.category,
					form: product.form,
					subCategory: product.subCategory,
					templateNumber: product.templateNumber,
					productType: product.productType,
					configurationNumber: product.configurationNumber,
					isSimulationCompleted: product.isSimulationCompleted,
					configErrMsg: product.configErrMsg,
					loader: product.loader,
					variableFormGroup: variableFormGroups[i],
					uniqueRef: Array.from(crypto.getRandomValues(new Uint8Array(16)))
						.map(b => b.toString(16).padStart(2, '0'))
						.join('')
				};
				products.push(productToAdd);
			}
		}
		return products;
	}

	private removeTranslationPrefix(value: string | undefined): string {
		if (!value || typeof value !== 'string') {
			return '';
		}
		return value.slice(Math.max(0, value.lastIndexOf('.') + 1));
	}

	private async restoreCachedOffer() {
		const offer = await this.db.getOffer();
		if (!offer) {
			return false;
		}
		const lastSavedOffer =
			offer.header.id === undefined
				? offerCreateDefault() // not saved already, then take default as reference
				: await firstValueFrom(this.offerService.getOffer(offerHeaderIdCreate(offer.header), true, 'NONE'));
		this.initOffer(offer, lastSavedOffer);
		return true;
	}

	private initOffer(offer: Offer, lastSavedOffer: Offer) {
		this.state = new AppWizardState(offer, lastSavedOffer);
		this.state.isValid = true;
	}

	private prepareVariableFormGroup(
		templateNumber: number,
		configData: ProductConfiguration,
		itemCount: number
	): FormGroup[] {
		const templateDetails = this.getState().ui.getTemplateNumberDetails(
			templateNumber
		);
		const variableFormGroupList: FormGroup[] = [];
		const variableFormGroup = new FormGroup({});
		if (templateDetails) {
			for (const variableName of Object.getOwnPropertyNames(templateDetails.properties)) {
				const variable = templateDetails.properties[variableName];
				this.prepareVariableFormGroupByType(
					variableFormGroup,
					variableName,
					configData,
					variable
				);
			}
		}
		variableFormGroupList.push(variableFormGroup);
		for (let i = 1; i < itemCount; i++) {
			const duplicateVariableFormGroup = new FormGroup({});
			for (const variableKey of Object.keys(variableFormGroup.controls)) {
				const variableControl = variableFormGroup.get(variableKey);
				if (variableControl instanceof FormControl) {
					if (variableControl.hasValidator(Validators.required)) {
						duplicateVariableFormGroup.addControl(
							variableKey,
							new FormControl(variableControl.value, Validators.required)
						);
					} else {
						duplicateVariableFormGroup.addControl(variableKey, new FormControl(variableControl.value));
					}
				} else if (variableControl instanceof FormArray) {
					const formArray = new FormArray<FormControl>([]);
					for (const control of variableControl.controls) {
						if (control.hasValidator(Validators.required)) {
							formArray.push(new FormControl(control.value, Validators.required));
						} else {
							formArray.push(new FormControl(control.value));
						}
					}
					duplicateVariableFormGroup.addControl(variableKey, formArray);
				}
			}
			variableFormGroupList.push(duplicateVariableFormGroup);
		}
		return variableFormGroupList;
	}

	private prepareVariableFormGroupByType(
		variableFormGroup: FormGroup,
		variableName: string,
		configData: ProductConfiguration,
		variable: TemplateVariable
	) {
		const productType = this.removeTranslationPrefix(configData.productType);
		switch (variable.type) {
		case 'array': {
			let arrayObject: CharacteristicValueSimpleObject[] = [];
			if (configData[variableName] && (productType !== PRODUCT_TYPE_NF || variable.hidden)) {
				arrayObject = <CharacteristicValueSimpleObject[]> configData[variableName];
			}
			const variableArray = <TemplateVariableArray>variable;
			const formArray = new FormArray<FormControl<string | number | null>>([]);

			variableFormGroup.addControl(variableName, formArray);
			const control = variableFormGroup.get(variableName);
			if (control instanceof FormArray) {
				this.prepareVariableFormGroupArray(
					control,
					arrayObject,
					variableArray,
					variable.mandatory
				);
			}
		}
			break;
		case 'number':
		case 'integer':
			if (variableName !== FIELD_THICKNESS && variableName !== FIELD_WIDTH
				&& variableName !== FIELD_LENGTH) {
				let value;
				if (productType !== PRODUCT_TYPE_NF || variable.hidden) {
					value = Number(configData[variableName]);
				}
				if (variable.mandatory) {
					variableFormGroup.addControl(
						variableName,
						new FormControl(value || undefined, Validators.required)
					);
				} else {
					variableFormGroup.addControl(
						variableName,
						new FormControl(value || undefined)
					);
				}
			}
			break;
		case 'string':
			if (variableName !== FIELD_SHIPTO_PARTY && variableName !== FIELD_STEEL_GRADE) {
				let value;
				if (productType !== PRODUCT_TYPE_NF || variable.hidden) {
					value = configData[variableName];
					if (value) {
						if (variable.free) {
							value = this.productCharacteristicsTranslationService.translate(
								!Array.isArray(value) ? String(value) : value.map(v => Object.values(v)).join(',')
							);
						} else if (typeof value === 'string') {
							value = this.removeTranslationPrefix(value);
						}
					}
				}
				if (variable.mandatory) {
					variableFormGroup.addControl(
						variableName,
						new FormControl(value || '', Validators.required)
					);
				} else {
					variableFormGroup.addControl(variableName, new FormControl(value || ''));
				}
			}
			break;
		case 'group':
			this.processGroupVariable(variableFormGroup, variable, configData);
			break;
		}
	}

	private processGroupVariable(
		variableFormGroup: FormGroup,
		groupVariable: TemplateVariable,
		configData: ProductConfiguration
	) {
		if (groupVariable.properties) {
			for (const variableName of Object.getOwnPropertyNames(groupVariable.properties)) {
				const variable = groupVariable.properties[variableName];
				this.prepareVariableFormGroupByType(
					variableFormGroup,
					variableName,
					configData,
					variable
				);
			}
		}
	}
}
